/**
 * PaginationControls Component
 * Reusable pagination UI with page navigation and page size selector
 */

import { ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight } from 'lucide-react';
import type { PaginationMeta } from '../types/pagination.type';

type PaginationControlsProps = {
  meta: PaginationMeta;
  onPageChange: (page: number) => void;
  onPageSizeChange: (pageSize: number) => void;
  pageSizeOptions?: number[];
  showPageInfo?: boolean;
  showTotalCount?: boolean;
};

/**
 * Pagination controls component with page navigation and size selector
 * 
 * @param meta - Pagination metadata from API response
 * @param onPageChange - Callback when page changes
 * @param onPageSizeChange - Callback when page size changes
 * @param pageSizeOptions - Available page sizes (default: [10, 25, 50, 100])
 * @param showPageInfo - Show "Page X of Y" text (default: true)
 * @param showTotalCount - Show total count (default: true)
 * 
 * Features:
 * - First/Previous/Next/Last page navigation
 * - Page size selector dropdown
 * - Displays total count reflecting filters
 * - Disabled states for unavailable actions
 */
export function PaginationControls({
  meta,
  onPageChange,
  onPageSizeChange,
  pageSizeOptions = [10, 25, 50, 100],
  showPageInfo = true,
  showTotalCount = true,
}: PaginationControlsProps) {
  const {
    page,
    pageSize,
    total,
    totalPages,
    hasPreviousPage,
    hasNextPage,
  } = meta;

  // Calculate range of items shown on current page
  const startItem = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const endItem = Math.min(page * pageSize, total);

  return (
    <div className="flex items-center justify-between px-4 py-3 bg-white border-t border-gray-200">
      {/* Left side - Total count and page info */}
      <div className="flex items-center gap-6 text-sm text-gray-600">
        {showTotalCount && (
          <div>
            <span className="font-medium text-gray-900">{total.toLocaleString('en-US')}</span>
            {' '}
            {total === 1 ? 'result' : 'results'}
          </div>
        )}
        
        {showPageInfo && total > 0 && (
          <div>
            Showing{' '}
            <span className="font-medium text-gray-900">
              {startItem.toLocaleString('en-US')}
            </span>
            {' '}to{' '}
            <span className="font-medium text-gray-900">
              {endItem.toLocaleString('en-US')}
            </span>
          </div>
        )}
      </div>

      {/* Right side - Page size selector and navigation */}
      <div className="flex items-center gap-4">
        {/* Page size selector */}
        <div className="flex items-center gap-2 text-sm text-gray-600">
          <span>Show</span>
          <select
            value={pageSize}
            onChange={(e) => onPageSizeChange(Number(e.target.value))}
            aria-label="Rows per page"
            className="px-2 py-1 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#026857] focus:border-transparent"
          >
            {pageSizeOptions.map((size) => (
              <option key={size} value={size}>
                {size}
              </option>
            ))}
          </select>
          <span>per page</span>
        </div>

        {/* Page navigation */}
        <div className="flex items-center gap-1">
          {/* First page */}
          <button
            type="button"
            onClick={() => onPageChange(1)}
            disabled={!hasPreviousPage}
            className="p-1.5 rounded-md hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            aria-label="First page"
          >
            <ChevronsLeft className="h-5 w-5 text-gray-600" />
          </button>

          {/* Previous page */}
          <button
            type="button"
            onClick={() => onPageChange(page - 1)}
            disabled={!hasPreviousPage}
            className="p-1.5 rounded-md hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            aria-label="Previous page"
          >
            <ChevronLeft className="h-5 w-5 text-gray-600" />
          </button>

          {/* Page indicator */}
          <div className="px-3 text-sm text-gray-700">
            <span className="font-medium">{page}</span>
            {totalPages > 0 && (
              <>
                {' '}of{' '}
                <span className="font-medium">{totalPages}</span>
              </>
            )}
          </div>

          {/* Next page */}
          <button
            type="button"
            onClick={() => onPageChange(page + 1)}
            disabled={!hasNextPage}
            className="p-1.5 rounded-md hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            aria-label="Next page"
          >
            <ChevronRight className="h-5 w-5 text-gray-600" />
          </button>

          {/* Last page */}
          <button
            type="button"
            onClick={() => onPageChange(totalPages)}
            disabled={!hasNextPage}
            className="p-1.5 rounded-md hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            aria-label="Last page"
          >
            <ChevronsRight className="h-5 w-5 text-gray-600" />
          </button>
        </div>
      </div>
    </div>
  );
}
